<template>
  <component
    :is="to ? 'router-link' : 'button'"
    class="dx-button"
    :class="[`dx-button--${variant}`, { 'is-block': block }]"
    :to="to"
    :type="to ? undefined : type"
    :disabled="to ? undefined : (disabled || loading)"
    :aria-busy="loading ? 'true' : undefined"
    @click="handleClick"
  >
    <span v-if="loading" class="dx-loader dx-loader--xs" aria-hidden="true"></span>
    <span class="dx-button__label"><slot></slot></span>
  </component>
</template>

<script>
export default {
  name: "LandingButton",
  props: {
    /* Optional — a submit button inside a form needs no click handler. */
    action: {
      type: Function,
      required: false,
      default: null,
    },
    to: {
      type: [String, Object],
      default: null,
    },
    type: {
      type: String,
      default: "button",
    },
    variant: {
      type: String,
      default: "raised",
      validator: (v) => ["raised", "accent", "ghost", "danger"].includes(v),
    },
    disabled: Boolean,
    loading: Boolean,
    block: Boolean,
  },
  methods: {
    handleClick(event) {
      if (this.disabled || this.loading) return;
      if (this.action) this.action(event);
    },
  },
};
</script>

<style scoped>
/*
 * This was a <div role="button" tabindex="0"> with a click handler and no
 * keydown handler, so it could not be operated by keyboard at all — which is
 * what left the Register form with no reachable submit control.
 */
.dx-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  font-family: var(--font-body);
  font-style: normal;
  font-size: var(--text-xs);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-label);
  padding: 0.7rem 1.25rem;
  border-radius: var(--radius-sm);
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  transition: transform var(--duration) var(--ease),
              box-shadow var(--duration) var(--ease),
              background-color var(--duration) var(--ease),
              border-color var(--duration) var(--ease);
  text-align: center;
  min-height: 44px;
  min-width: 90px;
  white-space: nowrap;
  position: relative;
}

.dx-button.is-block {
  width: 100%;
}

/* -- raised: the landing's physical keycap ------------------------------ */
.dx-button--raised {
  color: var(--text);
  background-image: var(--surface-raised);
  border: 2px solid var(--border-strong);
  border-top-color: var(--border-bevel);
  box-shadow: var(--shadow-raised);
}

.dx-button--raised:hover:not(:disabled) {
  background-image: linear-gradient(145deg, rgba(30, 38, 48, 0.98), rgba(18, 24, 32, 0.99));
  transform: translateY(-3px);
  box-shadow:
    0 8px 16px rgba(0, 0, 0, 0.5),
    0 4px 8px rgba(0, 0, 0, 0.7),
    inset 0 -5px 10px rgba(6, 10, 16, 0.95),
    inset 0 2px 4px rgba(40, 50, 60, 0.7);
  border-top-color: rgba(50, 60, 70, 0.8);
}

.dx-button--raised:active:not(:disabled) {
  transform: translateY(1px);
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.6),
    inset 0 4px 12px rgba(5, 8, 12, 0.95),
    inset 0 -2px 4px rgba(20, 28, 36, 0.5);
  background-image: linear-gradient(145deg, rgba(12, 16, 22, 0.99), rgba(20, 26, 34, 0.97));
  border-top-color: rgba(25, 35, 45, 0.9);
}

/* -- accent: the one primary action per screen -------------------------- */
.dx-button--accent {
  color: var(--text-on-accent);
  background: var(--accent);
  border: 2px solid var(--accent);
  box-shadow: 0 4px 14px rgba(0, 255, 200, 0.22);
}

.dx-button--accent:hover:not(:disabled) {
  background: var(--accent-strong);
  border-color: var(--accent-strong);
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(0, 255, 200, 0.3);
}

.dx-button--accent:active:not(:disabled) {
  transform: translateY(1px);
  box-shadow: 0 2px 6px rgba(0, 255, 200, 0.2);
}

/* -- ghost: secondary, low commitment ----------------------------------- */
.dx-button--ghost {
  color: var(--text);
  background: transparent;
  border: 1px solid var(--border-strong);
  box-shadow: none;
  min-height: 40px;
}

.dx-button--ghost:hover:not(:disabled) {
  border-color: var(--border-accent);
  background: var(--accent-wash);
  color: var(--accent);
}

/* -- danger: irreversible actions only ---------------------------------- */
.dx-button--danger {
  color: var(--danger);
  background: var(--danger-wash);
  border: 1px solid var(--danger-border);
  box-shadow: none;
}

.dx-button--danger:hover:not(:disabled) {
  background: color-mix(in srgb, var(--danger) 18%, transparent);
  border-color: var(--danger);
}

.dx-button--danger:active:not(:disabled) {
  transform: translateY(1px);
}

.dx-button:disabled,
.dx-button[aria-busy="true"] {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}

@media (min-width: 1200px) {
  .dx-button {
    font-size: var(--text-sm);
    padding: 0.8rem 1.6rem;
    min-width: 120px;
  }
}
</style>
